/**
 * Prowl line — shared component #2 (README "Shared components" list).
 * Source: Quote Flow Components.dc.html §1b.
 *
 * Real-data note: Prowl is a design-stage AI concierge concept — there is no
 * Prowl backend/reasoning engine in this codebase today. This component is
 * purely presentational: callers pass static, hand-authored copy (never a
 * live "AI insight"), same as every other page-level string in this repo.
 * See PR description judgment calls.
 */
interface ProwlLineProps {
  text: string;
  /** Inline underlined action at the end of the line (e.g. "Ask again"). */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * 'plain' = the under-input / inline-in-card form used everywhere in this
   * run. 'boxed' = the one reserved exception (S8's dismissible "Prowl's
   * read" card) — included for completeness since it's part of the shared
   * component, but nothing in S6/S7 uses it.
   */
  variant?: 'plain' | 'boxed';
  onDismiss?: () => void;
  className?: string;
}

const ProwlLine = ({
  text,
  actionLabel,
  onAction,
  variant = 'plain',
  onDismiss,
  className = '',
}: ProwlLineProps) => {
  if (variant === 'boxed') {
    return (
      <div
        className={`flex items-start gap-2.5 rounded-xl border border-[#95E7EF] bg-[#F7FEFF] px-4 py-3.5 ${className}`}
      >
        <span className="text-[#00B2D8] text-[15px] leading-[1.6] shrink-0">
          &#10022;
        </span>
        <p className="flex-1 text-[13.5px] leading-[1.6] text-[#2D617A]">
          {text}
          {actionLabel && (
            <>
              {' '}
              <button
                type="button"
                onClick={onAction}
                className="underline font-medium"
              >
                {actionLabel}
              </button>
            </>
          )}
        </p>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-gray-500 whitespace-nowrap shrink-0"
          >
            Dismiss
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-1.5 ${className}`}>
      <span className="text-[#00B2D8] text-sm leading-[1.55] shrink-0">
        &#10022;
      </span>
      <p className="text-[12.5px] leading-[1.55] text-[#317690]">
        {text}
        {actionLabel && (
          <>
            {' '}
            <button
              type="button"
              onClick={onAction}
              className="underline font-medium"
            >
              {actionLabel}
            </button>
          </>
        )}
      </p>
    </div>
  );
};

export default ProwlLine;
